import type { TFunction } from "i18next";

import {
  formatBoolean,
  hourToTimeValue,
  timeValueToHour,
  toNumber,
  toOptionalString,
  toStringValue,
} from "features/shared/lib/formatters";
import {
  formatMonthOptionLabel,
  formatMonthRangeText,
  fromApiMonthRanges,
  toApiMonthRanges,
} from "features/shared/lib/monthRanges";
import { formatNumberBasedOnUser, valueOrAlternative } from "utils/namesManipulation";

export type DrawerOptions = {
  displayOptions: string[];
  options: string[];
  placeholder?: string;
};

export type DrawerOptionsInput = {
  options?: readonly (number | string)[];
};

export type TechnicalConfigFieldKind = "boolean" | "enum" | "monthRanges" | "number" | "string" | "time";

export type TechnicalConfigFieldKindStrategy = {
  apiToFormValue: (rawValue: unknown) => boolean | string;
  formatCatalogValue: (value: unknown, t: TFunction) => string;
  formToApiValue: (formValue: boolean | string) => boolean | null | number | string;
  getDrawerOptions?: (field: DrawerOptionsInput) => DrawerOptions | undefined;
  getEmptyFormValue: () => boolean | string;
};

const EMPTY_DISPLAY = valueOrAlternative<string>("");

const emptyString = (): string => "";

const baseStrategies = {
  boolean: {
    apiToFormValue: (rawValue: unknown) => (typeof rawValue === "boolean" ? rawValue : false),
    formatCatalogValue: (value: unknown, t: TFunction) =>
      formatBoolean(t, typeof value === "boolean" ? value : undefined),
    formToApiValue: (formValue: boolean | string) => (typeof formValue === "boolean" ? formValue : null),
    getEmptyFormValue: () => false,
  },
  enum: {
    apiToFormValue: (rawValue: unknown) => (typeof rawValue === "string" ? rawValue : ""),
    formatCatalogValue: (value: unknown) => valueOrAlternative<string>(typeof value === "string" ? value : undefined),
    formToApiValue: (formValue: boolean | string) =>
      typeof formValue === "string" ? toOptionalString(formValue) ?? null : null,
    getDrawerOptions: (field: DrawerOptionsInput) =>
      field.options
        ? {
            displayOptions: field.options.map(String),
            options: field.options.map(String),
          }
        : undefined,
    getEmptyFormValue: emptyString,
  },
  number: {
    apiToFormValue: (rawValue: unknown) =>
      toStringValue(typeof rawValue === "number" || typeof rawValue === "string" ? rawValue : undefined),
    formatCatalogValue: (value: unknown) => formatNumberBasedOnUser(valueOrAlternative(value)),
    formToApiValue: (formValue: boolean | string) => {
      if (typeof formValue !== "string" || formValue.trim() === "") return null;
      return toNumber(formValue) ?? null;
    },
    getEmptyFormValue: emptyString,
  },
  string: {
    apiToFormValue: (rawValue: unknown) =>
      toStringValue(typeof rawValue === "number" || typeof rawValue === "string" ? rawValue : undefined),
    formatCatalogValue: (value: unknown) => valueOrAlternative<string>(typeof value === "string" ? value : undefined),
    formToApiValue: (formValue: boolean | string) =>
      typeof formValue === "string" ? toOptionalString(formValue) ?? null : null,
    getEmptyFormValue: emptyString,
  },
} satisfies Record<"boolean" | "enum" | "number" | "string", TechnicalConfigFieldKindStrategy>;

export const TECHNICAL_CONFIG_FIELD_KIND_STRATEGIES: Record<
  TechnicalConfigFieldKind,
  TechnicalConfigFieldKindStrategy
> = {
  ...baseStrategies,
  monthRanges: {
    apiToFormValue: (rawValue: unknown) => fromApiMonthRanges(typeof rawValue === "string" ? rawValue : undefined),
    formatCatalogValue: (value) => formatMonthRangeText(typeof value === "string" ? value : undefined) || EMPTY_DISPLAY,
    formToApiValue: (formValue) => (typeof formValue === "string" ? toApiMonthRanges(formValue) ?? null : null),
    getDrawerOptions: (field) =>
      field.options
        ? {
            displayOptions: field.options.map((month) => formatMonthOptionLabel(Number(month))),
            options: field.options.map(String),
          }
        : undefined,
    getEmptyFormValue: emptyString,
  },
  time: {
    apiToFormValue: (rawValue: unknown) => hourToTimeValue(typeof rawValue === "number" ? rawValue : undefined),
    formatCatalogValue: (value) => hourToTimeValue(typeof value === "number" ? value : undefined) || EMPTY_DISPLAY,
    formToApiValue: (formValue) => (typeof formValue === "string" ? timeValueToHour(formValue) ?? null : null),
    getEmptyFormValue: emptyString,
  },
};

export const getFieldKindStrategy = (kind: TechnicalConfigFieldKind): TechnicalConfigFieldKindStrategy =>
  TECHNICAL_CONFIG_FIELD_KIND_STRATEGIES[kind];
